import React from 'react';
import { TextFieldSimple, Button, Select } from '@soluto-design/react';
import { useFormContext, Controller } from 'react-hook-form';

const parseToNumber = (number: number | string) => {
    if (typeof number === 'string') {
        return Number(number);
    }

    return number;
};

export interface CalculatorFormValues {
    structureType: string;
    peakCapacity: string | number;
    specificCapex: string | number;
    specificOpex: string | number;
    returnRate: string | number;
}
interface CalculatorFormProps {
    location: {
        latitude: number;
        longitude: number;
    };

    onSubmit: (data: CalculatorFormValues) => void;
    className?: string;
    translations: {
        [key: string]: {
            text: string;
        };
    };
}

const structureTypes = ['sat', 'fixed', 'eastWest'];

const Form = ({
    location,
    onSubmit,
    className,
    translations,
}: CalculatorFormProps) => {
    const {
        handleSubmit,
        formState: { errors },
        control,
    } = useFormContext<CalculatorFormValues>();

    const options = structureTypes.reduce(function (
        result: { [key: string]: string },
        currentKey,
    ) {
        if (!result[currentKey]) {
            result[currentKey] =
                translations[
                    `calculator.form.structure.${currentKey.toLowerCase()}`
                ]?.text; // use translation
        }

        return result;
    },
    {});

    const thisOnSubmit = (data: CalculatorFormValues) => {
        const { latitude, longitude } = location ?? {};

        const finalData = {
            latitude,
            longitude,
            structureType: data?.structureType,
            peakCapacity: parseToNumber(data?.peakCapacity),
            specificCapex: parseToNumber(data?.specificCapex),
            specificOpex: parseToNumber(data?.specificOpex),
            returnRate: parseToNumber(data?.returnRate),
        };

        onSubmit(finalData);
    };

    const commonRules = {
        required: translations['calculator.form.required']?.text,
        validate: (value: any) =>
            !isNaN(Number(value)) || !value
                ? null
                : translations['calculator.form.invalid_format']?.text,
    };

    return (
        <form
            onSubmit={handleSubmit(thisOnSubmit)}
            {...(className && { className })}>
            <div className="grid grid-cols-8 gap-sm">
                <div className="col-span-full">
                    <Controller
                        name="structureType"
                        control={control}
                        render={({ field }) => (
                            <Select
                                {...field}
                                rightIcon="arrow-chevron--type-down"
                                label={
                                    translations[
                                        'calculator.form.structure.label'
                                    ]?.text
                                }
                                options={Object.entries(options).map(
                                    ([key, value]) => {
                                        return {
                                            value: key,
                                            label: value,
                                        };
                                    },
                                )}
                            />
                        )}
                    />
                </div>
                <div className="col-span-full lg:col-span-4">
                    <Controller
                        name="peakCapacity"
                        control={control}
                        rules={commonRules}
                        render={({ field }) => (
                            <TextFieldSimple
                                {...field}
                                type="number"
                                step="any"
                                error={errors?.[field?.name]?.message}
                                label={
                                    translations['calculator.form.peak.label']
                                        ?.text
                                }
                            />
                        )}
                    />
                </div>
                <div className="col-span-full lg:col-span-4">
                    <Controller
                        name="specificCapex"
                        control={control}
                        rules={commonRules}
                        render={({ field }) => {
                            return (
                                <TextFieldSimple
                                    {...field}
                                    type="number"
                                    step="any"
                                    error={errors?.[field?.name]?.message}
                                    label={
                                        translations[
                                            'calculator.form.capex.label'
                                        ]?.text
                                    }
                                />
                            );
                        }}
                    />
                </div>
                <div className="col-span-full lg:col-span-4">
                    <Controller
                        name="specificOpex"
                        control={control}
                        rules={commonRules}
                        render={({ field }) => (
                            <TextFieldSimple
                                {...field}
                                type="number"
                                step="any"
                                error={errors?.[field?.name]?.message}
                                label={
                                    translations['calculator.form.opex.label']
                                        ?.text
                                }
                            />
                        )}
                    />
                </div>

                <div className="col-span-full lg:col-span-4">
                    <Controller
                        name="returnRate"
                        control={control}
                        rules={commonRules}
                        render={({ field }) => (
                            <TextFieldSimple
                                {...field}
                                error={errors?.[field?.name]?.message}
                                label={
                                    translations[
                                        'calculator.form.returnrate.label'
                                    ]?.text
                                }
                            />
                        )}
                    />
                </div>
            </div>
            <div className="flex items-start mt-xg">
                <Button
                    type="submit"
                    variant="primary"
                    size="large"
                    component="button">
                    {translations['calculator.form.button.label']?.text}
                </Button>
            </div>
        </form>
    );
};

export default Form;
